import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchStaffSalary, updateStaffSalary } from '../../api/staff';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';
import StaffSalaryFields, { emptySalaryValues, salaryFromApi, salaryToApi } from './StaffSalaryFields';

const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;

const StaffSalaryPanel = ({ staffId, isOwner }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [values, setValues] = useState(emptySalaryValues());

  const { data, isLoading } = useQuery({
    queryKey: ['staff-salary', staffId],
    queryFn: () => fetchStaffSalary(staffId),
    enabled: !!staffId,
  });

  useEffect(() => {
    if (data?.salary) setValues(salaryFromApi(data.salary));
  }, [data?.salary]);

  const saveMut = useMutation({
    mutationFn: () => updateStaffSalary(staffId, salaryToApi(values)),
    onSuccess: (res) => {
      showToast(
        res.pending ? t('staff.salary.pendingSubmitted') : t('staff.salary.saved'),
        'success'
      );
      queryClient.invalidateQueries({ queryKey: ['staff-salary', staffId] });
      queryClient.invalidateQueries({ queryKey: ['staff-performance'] });
      queryClient.invalidateQueries({ queryKey: ['salary-change-requests'] });
    },
    onError: (err) => showToast(getFriendlyError(err, t('staff.salary.saveFailed')), 'error'),
  });

  if (!staffId) return null;

  const pending = data?.pendingChange;
  const salary = data?.salary;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <h3 className="text-sm font-medium">{t('staff.salary.title')}</h3>
      <p className="mt-1 text-xs text-zinc-500">{t('staff.salary.hint')}</p>

      {isLoading && <p className="mt-3 text-sm text-zinc-500">{t('common.loading')}</p>}

      {!isLoading && salary && (
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-zinc-200 px-3 py-2 dark:border-zinc-800">
            <p className="text-xs text-zinc-500">{t('staff.salary.daily')}</p>
            <p className="font-medium">{formatLkr(salary.dailyAmount)}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 px-3 py-2 dark:border-zinc-800">
            <p className="text-xs text-zinc-500">{t('staff.salary.weekly')}</p>
            <p className="font-medium">{formatLkr(salary.weeklyAmount)}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 px-3 py-2 dark:border-zinc-800">
            <p className="text-xs text-zinc-500">{t('staff.salary.monthly')}</p>
            <p className="font-medium">{formatLkr(salary.monthlyAmount)}</p>
          </div>
        </div>
      )}

      {pending && (
        <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-sm">
          <p className="font-medium text-amber-700 dark:text-amber-300">{t('staff.salary.pendingTitle')}</p>
          <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
            {t('staff.salary.pendingDetail', {
              name: pending.requestedByName,
              daily: formatLkr(pending.proposedSalary.dailyAmount),
              weekly: formatLkr(pending.proposedSalary.weeklyAmount),
              monthly: formatLkr(pending.proposedSalary.monthlyAmount),
            })}
          </p>
          {isOwner && (
            <p className="mt-2 text-xs text-zinc-500">{t('staff.salary.pendingOwnerHint')}</p>
          )}
        </div>
      )}

      <div className="mt-4">
        <p className="mb-2 text-xs font-medium text-zinc-500">{t('staff.salary.editTitle')}</p>
        <StaffSalaryFields values={values} onChange={setValues} t={t} />
        {!isOwner && (
          <p className="mt-2 text-xs text-zinc-500">{t('staff.salary.staffEditHint')}</p>
        )}
        <button
          type="button"
          disabled={saveMut.isPending || !!pending}
          onClick={() => saveMut.mutate()}
          className="mt-3 w-full rounded-lg bg-glow-600 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {saveMut.isPending ? t('common.saving') : isOwner ? t('staff.salary.save') : t('staff.salary.requestChange')}
        </button>
      </div>
    </div>
  );
};

export default StaffSalaryPanel;
